import {
  Col,
  Create,
  Edit,
  Form,
  FormProps,
  Input,
  ListButton,
  Row,
  Select,
  Switch
} from '@pankod/refine-antd';
import { usePermissions } from '@pankod/refine-core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useState } from 'react';
import { useOrganizationOptions } from 'hooks/useOrganizationOptions';
import { httpClient } from 'services/httpClient';
import { buildAssignableRoleOptions } from 'utils/buildAssignableRoleOptions';
import { buildPersonTitleOptions } from 'utils/buildPersonTitleOptions';

export const UserCreate: React.FC = () => {
  const { t } = useTranslation();
  const { action } = useParams();
  const { data: role } = usePermissions();
  const personTitleOptions = buildPersonTitleOptions(t);
  const assignableRoleOptions = buildAssignableRoleOptions(t, role);

  let FormAction = Edit;
  const passwordRules = [];

  if (action === 'create') {
    FormAction = Create;
    passwordRules.push({
      required: true,
      message: t('pages.userForm.fields.password.errors.required')
    });
  }

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const formProps: FormProps = { form };
  const saveButtonProps = { loading: saving, onClick: () => form.submit() };

  const { selectProps: organizationSelectProps } = useOrganizationOptions();

  formProps.initialValues = {
    // These values are only used for defaults; user input is still required.
    role: assignableRoleOptions?.[0]?.value,
    title: personTitleOptions?.[0]?.value,
    isActive: true
  };

  const handleFinish = async (values: any) => {
    setSaving(true);
    const normalizeEmptyToNull = (value: unknown) => {
      if (typeof value !== 'string') return value;
      return value.trim() === '' ? null : value;
    };

    const payload = {
      ...values,
      skype: normalizeEmptyToNull((values as any).skype),
      mobileNumber: normalizeEmptyToNull((values as any).mobileNumber),
      workPhone: normalizeEmptyToNull((values as any).workPhone)
    };

    try {
      await httpClient.post('/admin/users', payload);
      window.location.href = '/users';
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ||
        t('notifications.error', { statusCode: 500 });

      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormAction
      saveButtonProps={saveButtonProps}
      pageHeaderProps={{ extra: <ListButton /> }}
    >
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.organization.label')}
              name="orgId"
              rules={[
                {
                  required: true,
                  message: t(
                    'pages.userForm.fields.organization.errors.required'
                  )
                }
              ]}
            >
              <Select
                {...organizationSelectProps}
                placeholder={t(
                  'pages.userForm.fields.organization.placeholder'
                )}
              />
            </Form.Item>
          </Col>
          {!!assignableRoleOptions?.length && (
            <Col span={12}>
              <Form.Item
                label={t('pages.userForm.fields.role.label')}
                name="role"
                rules={[
                  {
                    required: true,
                    message: t('pages.userForm.fields.role.errors.required')
                  }
                ]}
              >
                <Select
                  options={assignableRoleOptions}
                  placeholder={t('pages.userForm.fields.role.placeholder')}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.firstName.label')}
              name="firstName"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.firstName.errors.required')
                }
              ]}
            >
              <Input
                addonBefore={
                  <Form.Item name="title">
                    <Select
                      placeholder={t('pages.userForm.fields.title.placeholder')}
                      options={personTitleOptions}
                      style={{ width: '120px' }}
                    />
                  </Form.Item>
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.lastName.label')}
              name="lastName"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.lastName.errors.required')
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.email.label')}
              name="email"
              rules={[
                {
                  required: true,
                  message: t('pages.userForm.fields.email.errors.required')
                },
                {
                  type: 'email',
                  message: t('pages.userForm.fields.email.errors.invalid')
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('pages.userForm.fields.password.label')}
              name="password"
              rules={passwordRules}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.skype.label')}
              name="skype"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.mobile.label')}
              name="mobileNumber"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('pages.userForm.fields.workPhone.label')}
              name="workPhone"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[40, 16]}>
          <Col span={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch defaultChecked={action === 'create'} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </FormAction>
  );
};
